import { useDispatch, useSelector } from "react-redux";
import { HtmlEditor, Inject, RichTextEditorComponent } from "@syncfusion/ej2-react-richtexteditor";
import { convertDate } from "../../../services/luxonHelper";
import css from "./IssueDetails.module.css";
import CommentsList from "../../Comments/CommentsList";
import ToolTipComponent from "../../../cubeComponent/html/tooltip";
import CustomFields from "../../IssuesCustomFields/CustomFields";
import { getViewerUrl } from "@Helpers";
import { useEffect, useState } from "react";
import { BiLinkExternal } from "react-icons/bi";
import { getIssuesData } from "../../../actions/bcfData";
import { getUrlForImage } from "../../../cubeComponent/constants/globalFunctions";
import { useNavigate } from "react-router-dom";
import { getStatusBtnStyle } from "../../Reviews/ReviewsHelper";
import { GrClose } from "react-icons/gr";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReviewsListForEntity from "../../Reviews/ReviewsListForEntity";
import { getIconFromFieldType } from "../../PortalSetings/Configuration/FormsAndFields/FormTemplate/constants";
import { useTranslation } from "react-i18next";
import { getImageUrl } from "@Utils";

function IssueDetails({ rowData, allowCloseBtn = false, closePropertiesCallback }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [accordionState, setAccordionState] = useState({ reviews: false });

  const fileVersionId = rowData.transmittalVersionId ? rowData.fileVersion : rowData.fileVersionId;
  const issueId = rowData.transmittalVersionId ? rowData.issueId : rowData.id;
  const { spaceId } = rowData;

  const issues = useSelector((state) => state.issues);

  const category = issues[issueId]?.statusInReview?.category || null;
  const style = getStatusBtnStyle(category);

  const attachedFiles = useSelector((state) => {
    const { issues } = state;

    if (issues[issueId]?.attachedFiles) {
      const files = issues[rowData.id].attachedFiles.map((file) => ({
        name: file.originalName,
        fileVersion: file.fileVersionId,
        resourceType: file.resourceType,
      }));

      return files;
    }

    return [];
  });

  const handleOpenFileLink = () => {
    const urlData = { fileVersionId, issueId, spaceId };

    // TODO: ISsues cannot be send in transmittal. need to check if below code can be remoevd
    if (!rowData.transmittalVersionId) {
      urlData.projectId = rowData.projectUniqueId;
    }

    const location = getViewerUrl(urlData);
    navigate(location);
  };

  const closePropertiesHandler = () => {
    if (closePropertiesCallback) {
      closePropertiesCallback();
    }
  };

  useEffect(() => {
    const params = { requiredObj: ["versionData", "filesData"], issueId };
    dispatch(getIssuesData(params));
  }, [issueId]);

  const handleOpenLink = (dependency, newTab) => {
    const location = getViewerUrl({
      fileVersionId: dependency.fileVersion,
      spaceId: issues[issueId].spaceId,
      projectId: issues[issueId].projectUniqueId,
    });
    newTab ? window.open(location, "_blank") : navigate(location);
  };

  const reviewsHeader = () => (
    <div>
      <p>{t("Reviews")}</p>
    </div>
  );

  const customFieldsHeader = () => (
    <div>
      <p>{t("Custom Fields")}</p>
    </div>
  );

  const reviewsContent = () => {
    const resource = { issueId, resourceType: "RESOURCE_TYPE_ISSUE" };
    return <ReviewsListForEntity resource={resource} spaceId={spaceId} includeDefaultReviews />;
  };

  const getAnswerValue = (answer, fieldType) => {
    if (!answer) return "-";

    const value = answer.value;

    if (fieldType == "date-time") {
      return convertDate(Number(value), "NormalDate") || "-";
    }
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (typeof item == "object") {
            return item.name;
          }
          return item;
        })
        .join(", ");
    }
    return value || "-";
  };

  const customFieldsContent = () => (
    <div className="flex flex-col py-2 gap-8">
      {rowData.formTemplate?.map((section) => (
        <>
          <div key={section._id} className="font-semibold text-base">
            {section.sectionHeading}
          </div>
          <div className="flex flex-col gap-6">
            {section.questionFields.map((field, index) => (
              <div key={index} className="flex">
                <div className="flex flex-1 justify-self-start font-semibold text-sm gap-2">
                  <span>{getIconFromFieldType(field.fieldType)}</span>
                  {field.questionValue}
                </div>
                <div className="flex-1 justify-self-start text-sm ms-6">
                  {getAnswerValue(field.answer, field.fieldType)}
                </div>
              </div>
            ))}
          </div>
        </>
      ))}
    </div>
  );

  const getSnapshotUrl = () => {
    let url = `${getImageUrl()}/DefaultProjectImage.svg`;
    if (issues[issueId].snapshot) {
      url = getUrlForImage({ id: issueId }, "IssueImage");
    }
    return url;
  };

  return (
    <div className={css.issueDetailsWrapper}>
      {issues[issueId] && (
        <div className={css.topContentStyle}>
          <div className={css.externalLinkWrapper}>
            {rowData.fileVersionId && (
              <div className={css.openExternalLink} onClick={handleOpenFileLink}>
                {t("View in Viewer")}
              </div>
            )}
            {allowCloseBtn ? <GrClose size={14} onClick={closePropertiesHandler} /> : undefined}
          </div>
          <div className={css.infoWrapper}>
            <div className={css.fontWeightBold}>{issues[issueId].title}</div>
            <div>
              <ToolTipComponent
                showTipPointer
                position="bottom"
                content={convertDate(Number(issues[issueId].createdDate), "dateForText")}
              >
                {convertDate(Number(issues[issueId].createdDate), "dateForTooltip")}
              </ToolTipComponent>
            </div>
          </div>
          <div className={css.imageWrapper}>
            <img src={getSnapshotUrl()} className={css.snapshot} />
          </div>
          <div className={css.descriptionWrapper}>
            <RichTextEditorComponent
              cssClass={css.issueDescription}
              readonly
              height={180}
              value={issues[issueId].description}
            >
              <Inject services={[HtmlEditor]} />
            </RichTextEditorComponent>
          </div>
          <div className={css.fieldDetails}>
            <div className={css.detailField}>
              <span className={css.fontWeightBold}>{t("Created by")}</span>
              <span>{issues[issueId].createdByName}</span>
            </div>
            <div className={css.detailField}>
              <span className={css.fontWeightBold}>{t("Assigned To")}</span>
              <span>{issues[issueId].assignedToUserName}</span>
            </div>
            <div className={css.detailField}>
              <span className={css.fontWeightBold}>{t("Status")}</span>
              <div className={css.issueStatusInReview} style={style}>
                <p>{issues[issueId].statusInReview?.name}</p>
              </div>
            </div>
            {attachedFiles.length ? (
              <>
                <div className={css.detailField}>
                  <span className={css.fontWeightBold}>{t("Attachments")}</span>
                </div>
                <div className={css.attachFilesData}>
                  {attachedFiles.map((file, index) => (
                    <div key={index} className={css.fileOpenContainer}>
                      <div className={css.dependencyLink} onClick={() => handleOpenLink(file, false)}>
                        {file.name}
                      </div>
                      <BiLinkExternal onClick={() => handleOpenLink(file, true)} />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              ""
            )}
            <div>
              <CustomFields currentViewIssueId={issueId} />
            </div>
          </div>

          {fileVersionId && (
            <div className={css.commentsWrapper}>
              <CommentsList
                issueId={issueId}
                offialCommentId={issues[issueId].officialCommentId}
                elements={issues[issueId].viewpoint ? JSON.parse(issues[issueId].viewpoint).selection : {}}
                fileVersionId={fileVersionId}
                showAddComment={false}
              />
            </div>
          )}

          <Accordion
            disableGutters
            expanded={accordionState.reviews}
            onChange={() => setAccordionState((prev) => ({ ...prev, reviews: !prev.reviews }))}
            className={css.defaultAccordionMUI}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className={css.defaultAccordionHeaderMUI}>
              {reviewsHeader()}
            </AccordionSummary>
            <AccordionDetails>{reviewsContent()}</AccordionDetails>
          </Accordion>

          {rowData.formTemplate && (
            <Accordion
              disableGutters
              expanded={accordionState.customFields}
              onChange={() => setAccordionState((prev) => ({ ...prev, customFields: !prev.customFields }))}
              className={css.defaultAccordionMUI}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={css.defaultAccordionHeaderMUI}>
                {customFieldsHeader()}
              </AccordionSummary>
              <AccordionDetails>{customFieldsContent()}</AccordionDetails>
            </Accordion>
          )}
        </div>
      )}
    </div>
  );
}

export default IssueDetails;
